import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Stack,
    Divider,
    Chip,
    Tooltip
} from '@mui/material';
import {
    ArrowLeft,
    Calendar,
    DollarSign,
    User,
    Home,
    Plus,
    Clock,
    CreditCard,
    AlertCircle,
    CheckCircle2,
    XCircle,
    FileText,
    RefreshCw,
    ShieldAlert,
    Users,
    Tag,
    ArrowRight,
    Building,
    Percent,
    ExternalLink,
    Briefcase
} from 'lucide-react';
import { getBookingById, confirmBooking } from '../../Services/Bookings/BookingsApis';

// Helper to format date cleanly
const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// Helper to format date with time
const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Helper to calculate nights between 2 dates
const calculateNights = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffTime = Math.abs(end - start);
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

// Unified Status Badge following brand theme & AGENTS.md rules
const StatusBadge = ({ status, type = 'booking' }) => {
    const s = (status || '').toLowerCase();

    let styles = { bgcolor: '#F5F5F5', color: '#64748B', border: '1px solid #E2E8F0' };

    if (type === 'payment') {
        if (s === 'completed' || s === 'success' || s === 'paid') {
            styles = { bgcolor: '#EDF5EB', color: '#2E7D32', border: '1px solid #D6EAD2' };
        } else if (s === 'pending' || s === 'created') {
            styles = { bgcolor: '#FFF7E6', color: '#B45309', border: '1px solid #FDE68A' };
        } else if (s === 'failed' || s === 'cancelled') {
            styles = { bgcolor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' };
        }
    } else if (type === 'refund') {
        if (s === 'approved' || s === 'completed' || s === 'processed') {
            styles = { bgcolor: '#EDF5EB', color: '#2E7D32', border: '1px solid #D6EAD2' };
        } else if (s === 'pending' || s === 'requested') {
            styles = { bgcolor: '#FFF7E6', color: '#B45309', border: '1px solid #FDE68A' };
        } else if (s === 'rejected' || s === 'failed') {
            styles = { bgcolor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' };
        }
    } else if (type === 'arrival') {
        if (s === 'checked_in' || s === 'arrived') {
            styles = { bgcolor: '#EDF5EB', color: '#2E7D32', border: '1px solid #D6EAD2' };
        } else if (s === 'pending') {
            styles = { bgcolor: '#FFF7E6', color: '#B45309', border: '1px solid #FDE68A' };
        } else if (s === 'cancelled' || s === 'no_show') {
            styles = { bgcolor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' };
        }
    } else {
        // booking or change request status
        if (['confirmed', 'completed', 'host_accepted', 'approved', 'date_change_approved'].includes(s)) {
            styles = { bgcolor: '#EDF5EB', color: '#2E7D32', border: '1px solid #D6EAD2' };
        } else if (['pending', 'awaiting_admin_confirmation', 'date_change_requested'].includes(s)) {
            styles = { bgcolor: '#FFF7E6', color: '#B45309', border: '1px solid #FDE68A' };
        } else if (['cancelled', 'rejected', 'date_change_rejected'].includes(s)) {
            styles = { bgcolor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' };
        } else if (['user_cancelled', 'user_cancelled_date_change_request'].includes(s)) {
            styles = { bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #CBD5E1' };
        }
    }

    const formatLabel = (val) => {
        if (!val) return 'N/A';
        const map = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected',
            'completed': 'Completed',
            'created': 'Created',
            'host_accepted': 'Host Accepted',
            'date_change_requested': 'Date Change Requested',
            'date_change_rejected': 'Date Change Rejected',
            'date_change_approved': 'Date Change Approved',
            'user_cancelled': 'User Cancelled',
            'user_cancelled_date_change_request': 'User Cancelled Date Change',
            'awaiting_admin_confirmation': 'Awaiting Admin Confirmation',
            'checked_in': 'Checked In',
            'no_show': 'No Show'
        };
        return map[val.toLowerCase()] || val.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    return (
        <Box
            sx={{
                px: 1.2,
                py: 0.35,
                borderRadius: '16px',
                fontSize: '0.74rem',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                whiteSpace: 'nowrap',
                ...styles
            }}
        >
            {formatLabel(status)}
        </Box>
    );
};

// Reusable Super-Admin Key-Value Info Row (Pixel-Perfect Alignment)
const InfoRow = ({ label, value, chip, isLast = false, highlight = false, valueColor }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.1,
            borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
            gap: 2
        }}
    >
        <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontWeight: 500, minWidth: '130px', flexShrink: 0 }}>
            {label}
        </Typography>
        <Box sx={{ textAlign: 'right', minWidth: 0 }}>
            {chip ? chip : (
                <Typography
                    sx={{
                        fontSize: '0.84rem',
                        color: valueColor || (highlight ? '#2D1810' : '#1E293B'),
                        fontWeight: highlight ? 600 : 400,
                        overflowWrap: 'anywhere'
                    }}
                >
                    {value || '—'}
                </Typography>
            )}
        </Box>
    </Box>
);

const BookingDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const [searchParams] = useSearchParams();

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [confirmError, setConfirmError] = useState(null);

    // Resolve booking ID from multiple sources
    const queryId = searchParams.get('id') || searchParams.get('bookingId');
    const resolvedBookingId = routeId || queryId || location.state?.booking?.id || location.state?.id || location.state?.bookingId;

    const fetchBookingDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!resolvedBookingId) {
                throw new Error('Booking ID is missing. Please select a booking from the list.');
            }

            const numericBookingId = Number(resolvedBookingId);
            if (!Number.isInteger(numericBookingId) || numericBookingId <= 0) {
                throw new Error('Invalid booking ID: must be a positive integer');
            }

            const data = await getBookingById(numericBookingId);
            setBooking(data);
        } catch (err) {
            console.error('Error fetching booking details:', err);
            setError(err.message || 'Failed to fetch booking details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookingDetails();
    }, [resolvedBookingId]);

    const handleConfirmBooking = async () => {
        if (!booking?.id) return;
        setConfirming(true);
        setConfirmError(null);
        setSuccessMessage(null);

        try {
            await confirmBooking(booking.id);
            setSuccessMessage('Booking confirmed successfully');
            const updated = await getBookingById(booking.id);
            setBooking(updated);
        } catch (err) {
            console.error('Error confirming booking:', err);
            setConfirmError(err.message || 'Failed to confirm booking');
        } finally {
            setConfirming(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '65vh', bgcolor: '#FBFBFB' }}>
                <CircularProgress sx={{ color: '#6E8964' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Button
                    onClick={() => navigate('/bookings')}
                    startIcon={<ArrowLeft size={16} />}
                    sx={{ textTransform: 'none', color: '#64748B', mb: 2 }}
                >
                    Back to Bookings
                </Button>
                <Alert severity="error" sx={{ mb: 2, border: '1px solid #FECACA', borderRadius: '6px' }}>{error}</Alert>
                <Button onClick={fetchBookingDetails} variant="outlined" sx={{ textTransform: 'none', color: '#2D1810', borderColor: '#EAE5D9' }}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (!booking) {
        return (
            <Box sx={{ p: 3, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Button
                    onClick={() => navigate('/bookings')}
                    startIcon={<ArrowLeft size={16} />}
                    sx={{ textTransform: 'none', color: '#64748B', mb: 2 }}
                >
                    Back to Bookings
                </Button>
                <Alert severity="info" sx={{ borderRadius: '6px' }}>No booking data available.</Alert>
            </Box>
        );
    }

    // Calculation constants
    const currency = booking.currency || 'INR';
    const nights = calculateNights(booking.startDate, booking.endDate);
    const guestName = booking.userName || booking.cuName || booking.members?.[0]?.name || 'N/A';
    const retreatName = booking.retreatTitle || booking.retreat?.title || (booking.retreatId ? `Retreat #${booking.retreatId}` : 'N/A');
    const accommodationName = booking.accommodationTitle || booking.accommodation?.title || (booking.accommodationId ? `Accommodation #${booking.accommodationId}` : 'N/A');
    const hasRefund = Boolean(booking.refundDetails);
    const hasChangeRequests = Boolean(booking.changeRequests && booking.changeRequests.length > 0);

    return (
        <Box sx={{ width: '100%', p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh', boxSizing: 'border-box' }}>
            
            {/* ── Top Bar: Back & Refresh ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button
                    onClick={() => navigate('/bookings')}
                    startIcon={<ArrowLeft size={16} />}
                    disableElevation
                    disableRipple
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.84rem',
                        fontWeight: 500,
                        color: '#64748B',
                        px: 0,
                        py: 0.5,
                        '&:hover': {
                            bgcolor: 'transparent',
                            color: '#1E293B',
                        }
                    }}
                >
                    Back to Bookings
                </Button>

                <Button
                    onClick={fetchBookingDetails}
                    startIcon={<RefreshCw size={14} />}
                    size="small"
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.78rem',
                        fontWeight: 500,
                        color: '#78716C',
                        border: '1px solid #EAE5D9',
                        borderRadius: '6px',
                        bgcolor: '#FFFFFF',
                        px: 1.5,
                        py: 0.5,
                        '&:hover': {
                            bgcolor: '#FAF8F5',
                            borderColor: '#D6CFC3'
                        }
                    }}
                >
                    Refresh Data
                </Button>
            </Box>

            {/* ── Super Admin Header Banner ── */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography
                                sx={{
                                    color: '#2D1810',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.4rem', md: '1.75rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Booking #{booking.bookingId || booking.id}
                            </Typography>
                            <StatusBadge status={booking.bookingStatus} type="booking" />
                            <StatusBadge status={booking.paymentStatus} type="payment" />
                            {booking.arrivalStatus && (
                                <StatusBadge status={booking.arrivalStatus} type="arrival" />
                            )}
                            {booking.hasChangeRequest && (
                                <Box sx={{ px: 1.2, py: 0.35, borderRadius: '16px', fontSize: '0.72rem', fontWeight: 500, bgcolor: '#FFF7E6', color: '#B45309', border: '1px solid #FDE68A' }}>
                                    Change Request Logged
                                </Box>
                            )}
                        </Box>
                        <Typography sx={{ color: '#64748B', fontSize: '0.84rem', mt: 0.6, fontWeight: 400 }}>
                            Internal Record #{booking.id} • Direct Reservation • Booked on {formatDateTime(booking.createdAt)}
                        </Typography>
                    </Box>
                </Box>

                {/* Notifications / Alerts */}
                {successMessage && (
                    <Alert severity="success" sx={{ mt: 2, border: '1px solid #D6EAD2', borderRadius: '6px' }}>{successMessage}</Alert>
                )}
                {confirmError && (
                    <Alert severity="error" sx={{ mt: 2, border: '1px solid #FECACA', borderRadius: '6px' }}>{confirmError}</Alert>
                )}
            </Box>

            {/* ── Connected Super-Admin Metric Highlights Strip ── */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
                    border: '1px solid #EAE5D9',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: '#FFFFFF',
                    mb: 3
                }}
            >
                <Box sx={{ p: 2, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', md: 'none' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Gross Booking Total
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#2D1810', mt: 0.5 }}>
                        {currency} {booking.totalAmount || '0.00'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#64748B', mt: 0.25 }}>
                        {nights ? `${nights} Nights Stay` : 'Scheduled Stay'}
                    </Typography>
                </Box>

                <Box sx={{ p: 2, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', md: 'none' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Deposit Paid
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#2E7D32', mt: 0.5 }}>
                        {currency} {booking.amountPaid || '0.00'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#2E7D32', mt: 0.25 }}>
                        {booking.paymentStatus === 'completed' ? 'Fully Collected' : 'Payment Processing'}
                    </Typography>
                </Box>

                <Box sx={{ p: 2, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', md: 'none' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Due Balance
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: parseFloat(booking.amountDue || 0) > 0 ? '#B91C1C' : '#64748B', mt: 0.5 }}>
                        {currency} {booking.amountDue || '0.00'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#64748B', mt: 0.25 }}>
                        Due Status: {booking.duePaymentStatus || 'pending'}
                    </Typography>
                </Box>

                <Box sx={{ p: 2, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', sm: 'none' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        App Share (Platform)
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#6E8964', mt: 0.5 }}>
                        {currency} {booking.appAmount || '0.00'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#64748B', mt: 0.25 }}>
                        Comm: {booking.visibilityCommission || '0'}% (Plan #{booking.visibilityPlanId || '—'})
                    </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Host Payout
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#2D1810', mt: 0.5 }}>
                        {currency} {booking.hostAmount || '0.00'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#64748B', mt: 0.25 }}>
                        Net payable to property
                    </Typography>
                </Box>
            </Box>

            {/* ── Main Two-Column Layout (CSS Grid for Strict Pixel-Perfect Alignment) ── */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 350px' },
                    gap: 3,
                    alignItems: 'start',
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* ══════════ LEFT COLUMN: Detailed Operational & Financial Sections ══════════ */}
                <Stack spacing={3} sx={{ minWidth: 0, width: '100%' }}>

                    {/* SECTION 1: SUPER-ADMIN CHANGE REQUEST MANAGEMENT */}
                    {hasChangeRequests && (
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                p: 2,
                                px: 2.5,
                                borderBottom: '1px solid #EAE5D9',
                                bgcolor: '#FAF8F5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Clock size={18} color="#D97706" />
                                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2D1810' }}>
                                        Change Requests History
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.78rem', color: '#78716C', fontWeight: 500 }}>
                                    {booking.changeRequests.length} Record{booking.changeRequests.length > 1 ? 's' : ''} Attached
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2.5 }}>
                                <Stack spacing={2.5}>
                                    {booking.changeRequests.map((cr, idx) => (
                                        <Box
                                            key={cr.id || idx}
                                            sx={{
                                                borderRadius: '6px',
                                                border: '1px solid #EAE5D9',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {/* Subheader */}
                                            <Box sx={{
                                                p: 1.5,
                                                px: 2,
                                                bgcolor: '#FBFBFB',
                                                borderBottom: '1px solid #EAE5D9',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}>
                                                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#1E293B' }}>
                                                    Request #{cr.id} — {cr.requestType === 'date_change' ? 'Date Rescheduling Request' : (cr.requestType || 'Booking Modification')}
                                                </Typography>
                                                <StatusBadge status={cr.status} type="booking" />
                                            </Box>

                                            {/* Structured 3-Column Schedule Comparison Table */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                                                    borderBottom: '1px solid #EAE5D9',
                                                    bgcolor: '#FFFFFF'
                                                }}
                                            >
                                                {/* Column 1: Current Booking */}
                                                <Box sx={{ p: 2, borderRight: { sm: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', sm: 'none' } }}>
                                                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Original Schedule
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mt: 0.5 }}>
                                                        {formatDate(cr.currentStartDate)} – {formatDate(cr.currentEndDate)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mt: 0.25 }}>
                                                        Total: {currency} {cr.currentPrice}
                                                    </Typography>
                                                </Box>

                                                {/* Column 2: Requested Change */}
                                                <Box sx={{ p: 2, borderRight: { sm: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', sm: 'none' }, bgcolor: '#FFFDF7' }}>
                                                    <Typography sx={{ fontSize: '0.72rem', color: '#D97706', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Requested Schedule
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mt: 0.5 }}>
                                                        {formatDate(cr.requestedStartDate)} – {formatDate(cr.requestedEndDate)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mt: 0.25 }}>
                                                        Price: {currency} {cr.requestedPrice}
                                                    </Typography>
                                                </Box>

                                                {/* Column 3: Variance & Pricing Impact */}
                                                <Box sx={{ p: 2, bgcolor: '#FFFFFF' }}>
                                                    <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Price Difference
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: parseFloat(cr.priceDifference || 0) === 0 ? '#2E7D32' : '#B91C1C', mt: 0.5 }}>
                                                        {currency} {cr.priceDifference || '0.00'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mt: 0.25 }}>
                                                        No Additional Charge
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Notes, Reasons & Super Admin Audit */}
                                            <Box sx={{ p: 2, bgcolor: '#FBFBFB' }}>
                                                <Stack spacing={1}>
                                                    {cr.guestReason && (
                                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 500, minWidth: '120px' }}>
                                                                Guest's Reason:
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.82rem', color: '#1E293B' }}>
                                                                {cr.guestReason}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {cr.hostDecisionReason && (
                                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 500, minWidth: '120px' }}>
                                                                Host Note / Reason:
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.82rem', color: '#1E293B' }}>
                                                                {cr.hostDecisionReason}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    <Divider sx={{ my: 0.5, borderColor: '#F0ECE1' }} />

                                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pt: 0.5 }}>
                                                        <Typography sx={{ fontSize: '0.74rem', color: '#78716C' }}>
                                                            Requested: {formatDateTime(cr.createdAt)} by {cr.cuName || 'Guest'} (#{cr.cuId})
                                                        </Typography>
                                                        {cr.decisionAt && (
                                                            <Typography sx={{ fontSize: '0.74rem', color: '#78716C' }}>
                                                                Decision Logged: {formatDateTime(cr.decisionAt)}
                                                            </Typography>
                                                        )}
                                                        {cr.expiresAt && (
                                                            <Typography sx={{ fontSize: '0.74rem', color: '#78716C' }}>
                                                                Request Expiry: {formatDateTime(cr.expiresAt)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Paper>
                    )}

                    {/* SECTION 2: CANCELLATION & REFUND AUDIT CARD */}
                    {hasRefund && (
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{
                                p: 2,
                                px: 2.5,
                                borderBottom: '1px solid #EAE5D9',
                                bgcolor: '#FAF8F5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <ShieldAlert size={18} color="#B45309" />
                                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#803927' }}>
                                        Cancellation & Refund Audit
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#78716C' }}>
                                        Ref: #{booking.refundDetails.refundId}
                                    </Typography>
                                    <StatusBadge status={booking.refundDetails.status} type="refund" />
                                </Box>
                            </Box>

                            <Box sx={{ p: 2.5 }}>
                                {/* 4-Segmented Connected Metric Cards */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                                        border: '1px solid #EAE5D9',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        mb: 2
                                    }}
                                >
                                    <Box sx={{ p: 1.75, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', md: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase' }}>
                                            Total Booking Base
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1E293B', mt: 0.3 }}>
                                            {booking.refundDetails.currency || currency} {booking.refundDetails.totalBookingAmount}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25 }}>Amount paid prior</Typography>
                                    </Box>

                                    <Box sx={{ p: 1.75, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', md: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase' }}>
                                            Cancellation Fee
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#B91C1C', mt: 0.3 }}>
                                            {booking.refundDetails.currency || currency} {booking.refundDetails.cancellationFee}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#B91C1C', mt: 0.25 }}>Retained by policy</Typography>
                                    </Box>

                                    <Box sx={{ p: 1.75, borderRight: { md: '1px solid #EAE5D9' }, borderBottom: { xs: '1px solid #EAE5D9', sm: 'none' }, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase' }}>
                                            Refund Payable ({booking.refundDetails.refundPercentage}%)
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2E7D32', mt: 0.3 }}>
                                            {booking.refundDetails.currency || currency} {booking.refundDetails.refundAmount}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#2E7D32', mt: 0.25 }}>Calculated refund</Typography>
                                    </Box>

                                    <Box sx={{ p: 1.75, bgcolor: '#FFFFFF' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#78716C', fontWeight: 500, textTransform: 'uppercase' }}>
                                            Settlement Status
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: booking.refundDetails.status === 'REJECTED' ? '#B91C1C' : '#2E7D32', mt: 0.3 }}>
                                            {booking.refundDetails.status || 'N/A'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25 }}>Decision finalized</Typography>
                                    </Box>
                                </Box>

                                {/* Reason Box */}
                                <Box sx={{ p: 1.5, px: 2, bgcolor: '#FBFBFB', borderRadius: '6px', border: '1px solid #EAE5D9', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontWeight: 500, minWidth: '150px' }}>
                                        Cancellation Reason:
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 400 }}>
                                        {booking.refundDetails.cancellationReason || 'No specific cancellation reason logged'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}

                    {/* SECTION 3: STAY SCHEDULE & RETREAT DETAILS */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            p: 2,
                            px: 2.5,
                            borderBottom: '1px solid #EAE5D9',
                            bgcolor: '#FAF8F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Home size={18} color="#6E8964" />
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2D1810' }}>
                                    Stay Schedule & Retreat Accommodation
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ p: 2.5 }}>
                            <Grid container spacing={3}>
                                {/* Left Sub-column: Retreat & Accommodation */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                        Property & Room Option
                                    </Typography>
                                    <Stack spacing={0}>
                                        <InfoRow label="Retreat Name" value={retreatName} highlight />
                                        <InfoRow label="Retreat ID" value={`#${booking.retreatId || '—'}`} />
                                        <InfoRow label="Accommodation" value={accommodationName} highlight />
                                        <InfoRow label="Accommodation ID" value={`#${booking.accommodationId || '—'}`} />
                                        <InfoRow
                                            label="Nightly Price"
                                            value={`${currency} ${booking.accommodationPrice || booking.accommodation?.price || '0.00'} / Night`}
                                            valueColor="#2E7D32"
                                            isLast
                                        />
                                    </Stack>
                                </Grid>

                                {/* Right Sub-column: Schedule & Timing */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                        Dates & Check-in Schedule
                                    </Typography>
                                    <Stack spacing={0}>
                                        <InfoRow label="Check-in Date" value={formatDate(booking.startDate)} />
                                        <InfoRow label="Check-out Date" value={formatDate(booking.endDate)} />
                                        <InfoRow
                                            label="Length of Stay"
                                            value={nights !== null ? `${nights} Night${nights > 1 ? 's' : ''}` : '—'}
                                            valueColor="#2E7D32"
                                        />
                                        <InfoRow
                                            label="Arrival Status"
                                            chip={<StatusBadge status={booking.arrivalStatus || 'pending'} type="arrival" />}
                                        />
                                        <InfoRow label="Booking Created" value={formatDateTime(booking.createdAt)} isLast />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>

                    {/* SECTION 4: SUPER ADMIN ACCOUNTING & REVENUE SPLIT */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            p: 2,
                            px: 2.5,
                            borderBottom: '1px solid #EAE5D9',
                            bgcolor: '#FAF8F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <DollarSign size={18} color="#2E7D32" />
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2D1810' }}>
                                    Financial Summary & Platform Revenue Split
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StatusBadge status={booking.paymentStatus} type="payment" />
                                <Box sx={{ px: 1.2, py: 0.35, borderRadius: '16px', fontSize: '0.72rem', fontWeight: 500, bgcolor: booking.duePaymentStatus === 'completed' ? '#EDF5EB' : '#FFF7E6', color: booking.duePaymentStatus === 'completed' ? '#2E7D32' : '#B45309', border: '1px solid #EAE5D9' }}>
                                    Due: {booking.duePaymentStatus || 'pending'}
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ p: 2.5 }}>
                            <Grid container spacing={3}>
                                {/* Column 1: Guest Invoicing Details */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                        Guest Invoice & Payments
                                    </Typography>
                                    <Stack spacing={0}>
                                        <InfoRow label="Total Booking Price" value={`${currency} ${booking.totalAmount || '0.00'}`} highlight />
                                        <InfoRow label="Amount Paid (Deposit)" value={`${currency} ${booking.amountPaid || '0.00'}`} valueColor="#2E7D32" />
                                        <InfoRow
                                            label="Amount Due"
                                            value={`${currency} ${booking.amountDue || '0.00'}`}
                                            valueColor={parseFloat(booking.amountDue || 0) > 0 ? '#B91C1C' : '#64748B'}
                                            highlight={parseFloat(booking.amountDue || 0) > 0}
                                        />
                                        <InfoRow label="Discount Amount" value={`${currency} ${booking.discountAmount || '0.00'}`} />
                                        <InfoRow label="GST / Taxes" value={booking.gstAmount ? `${currency} ${booking.gstAmount}` : 'Included / 0.00'} isLast />
                                    </Stack>
                                </Grid>

                                {/* Column 2: Platform Commission & Host Disbursement */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                        Platform Split & Settlement
                                    </Typography>
                                    <Stack spacing={0}>
                                        <InfoRow label="App Share (Earned)" value={`${currency} ${booking.appAmount || '0.00'}`} valueColor="#2E7D32" highlight />
                                        <InfoRow label="Host Payout (Payable)" value={`${currency} ${booking.hostAmount || '0.00'}`} highlight />
                                        <InfoRow
                                            label="Visibility Commission"
                                            value={`${booking.visibilityCommission || '0'}% (Plan #${booking.visibilityPlanId || '—'})`}
                                        />
                                        <InfoRow label="Coordination Charges" value={booking.coordinationCharges ? `${currency} ${booking.coordinationCharges}` : 'None (0.00)'} />
                                        <InfoRow label="Payment Mode" value={booking.paymentType ? booking.paymentType.toUpperCase() : 'DEPOSIT'} isLast />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>

                    {/* SECTION 5: PAYMENT TRANSACTIONS TABLE */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            p: 2,
                            px: 2.5,
                            borderBottom: '1px solid #EAE5D9',
                            bgcolor: '#FAF8F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <CreditCard size={18} color="#6E8964" />
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2D1810' }}>
                                    Payment Transactions
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#78716C', fontWeight: 500 }}>
                                {booking.paymentTransactions?.length || 0} Logged
                            </Typography>
                        </Box>

                        {booking.paymentTransactions && booking.paymentTransactions.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#FBFBFB', borderBottom: '1px solid #EAE5D9' }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', py: 1.25, px: 2.5 }}>TRANSACTION ID</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', py: 1.25, px: 2 }}>GATEWAY</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', py: 1.25, px: 2, textAlign: 'center' }}>AMOUNT</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', py: 1.25, px: 2, textAlign: 'center' }}>STATUS</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', py: 1.25, px: 2.5, textAlign: 'right' }}>DATE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {booking.paymentTransactions.map((tx) => (
                                            <TableRow
                                                key={tx.id}
                                                sx={{
                                                    '& td': { borderBottom: '1px solid #F1F5F9' },
                                                    '&:last-child td': { borderBottom: 0 }
                                                }}
                                            >
                                                <TableCell sx={{ py: 1.5, px: 2.5, fontSize: '0.84rem', color: '#1E293B', fontWeight: 500 }}>
                                                    #{tx.id}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.84rem', color: '#1E293B', textTransform: 'capitalize' }}>
                                                    {tx.gateway || 'razorpay'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5, px: 2, textAlign: 'center', fontSize: '0.84rem', fontWeight: 600, color: '#2E7D32' }}>
                                                    {currency} {tx.amount || booking.amountPaid || '0.00'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5, px: 2, textAlign: 'center' }}>
                                                    <StatusBadge status={tx.status || booking.paymentStatus} type="payment" />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.5, px: 2.5, textAlign: 'right', fontSize: '0.82rem', color: '#64748B' }}>
                                                    {formatDate(tx.createdAt || booking.createdAt)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#FBFBFB' }}>
                                <Typography sx={{ color: '#78716C', fontSize: '0.84rem' }}>No payment transaction logs available.</Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* SECTION 6: SELECTED ADD-ONS & SERVICES */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            p: 2,
                            px: 2.5,
                            borderBottom: '1px solid #EAE5D9',
                            bgcolor: '#FAF8F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Plus size={18} color="#6E8964" />
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2D1810' }}>
                                    Selected Add-ons & Extra Services
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#78716C', fontWeight: 500 }}>
                                {booking.addons?.length || 0} Item{booking.addons?.length === 1 ? '' : 's'}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.5 }}>
                            {booking.addons && booking.addons.length > 0 ? (
                                <Stack spacing={1.5}>
                                    {booking.addons.map((addon) => (
                                        <Box
                                            key={addon.id || addon.name}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                p: 1.75,
                                                bgcolor: '#FBFBFB',
                                                borderRadius: '6px',
                                                border: '1px solid #EAE5D9'
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 500, color: '#1E293B', fontSize: '0.88rem' }}>
                                                    {addon.name}
                                                </Typography>
                                                {addon.id && (
                                                    <Typography sx={{ color: '#78716C', fontSize: '0.74rem', mt: 0.25 }}>
                                                        Catalog Item ID: #{addon.id}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontWeight: 600, color: '#2E7D32', fontSize: '0.92rem' }}>
                                                {currency} {addon.price}
                                            </Typography>
                                        </Box>
                                    ))}

                                    <Box sx={{ mt: 1, p: 1.5, px: 2, bgcolor: '#FAF8F5', borderRadius: '6px', border: '1px solid #EAE5D9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 500, color: '#2D1810', fontSize: '0.84rem' }}>
                                            Total Add-on Charges:
                                        </Typography>
                                        <Typography sx={{ fontWeight: 600, color: '#2E7D32', fontSize: '0.95rem' }}>
                                            {currency} {booking.addons.reduce((sum, item) => sum + parseFloat(item.price || 0), 0).toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ) : (
                                <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#FBFBFB', borderRadius: '6px' }}>
                                    <Typography sx={{ color: '#78716C', fontSize: '0.84rem' }}>
                                        No optional add-ons selected for this booking.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                </Stack>

                {/* ══════════ RIGHT COLUMN: Super-Admin Control & Guest Sidebar (Fixed 350px) ══════════ */}
                <Stack spacing={3} sx={{ width: '100%' }}>

                    {/* SIDEBAR CARD 1: SUPER ADMIN ACTIONS */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2, px: 2.25, borderBottom: '1px solid #EAE5D9', bgcolor: '#FAF8F5' }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2D1810' }}>
                                Super Admin Actions
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.25 }}>
                            <Stack spacing={1.5}>
                                {/* Confirm Booking Button - Only when bookingStatus === "host_accepted" */}
                                {(booking.bookingStatus || '').toLowerCase() === 'host_accepted' ? (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disableElevation
                                        disableRipple
                                        onClick={handleConfirmBooking}
                                        disabled={confirming}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.84rem',
                                            fontWeight: 500,
                                            color: '#FFFFFF',
                                            border: '1px solid #6E8964',
                                            borderRadius: '6px',
                                            py: 0.9,
                                            boxShadow: 'none',
                                            bgcolor: '#6E8964',
                                            '&:hover': {
                                                bgcolor: '#5A7351',
                                                borderColor: '#4A5F42',
                                                boxShadow: 'none'
                                            },
                                            '&:disabled': {
                                                bgcolor: '#AAB5A5',
                                                borderColor: '#9AA896'
                                            }
                                        }}
                                    >
                                        {confirming ? 'Confirming...' : 'Confirm Host-Accepted Booking'}
                                    </Button>
                                ) : (
                                    <Box sx={{ p: 1.25, bgcolor: '#FBFBFB', borderRadius: '6px', border: '1px solid #EAE5D9', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                                            Current Status: <strong style={{ textTransform: 'capitalize', color: '#1E293B' }}>{booking.bookingStatus || 'N/A'}</strong>
                                        </Typography>
                                    </Box>
                                )}

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    disableElevation
                                    disableRipple
                                    onClick={() => navigate('/bookings')}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.84rem',
                                        fontWeight: 500,
                                        color: '#2D1810',
                                        border: '1px solid #EAE5D9',
                                        borderRadius: '6px',
                                        py: 0.85,
                                        boxShadow: 'none',
                                        bgcolor: '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: '#FAF8F5',
                                            borderColor: '#D6CFC3',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Back to Bookings Roster
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>

                    {/* SIDEBAR CARD 2: GUEST & CONTACT DETAILS */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2, px: 2.25, borderBottom: '1px solid #EAE5D9', bgcolor: '#FAF8F5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <User size={16} color="#6E8964" />
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2D1810' }}>
                                    Guest & Contact
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#78716C' }}>
                                User #{booking.userId || '—'}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.25 }}>
                            <Stack spacing={0}>
                                <InfoRow label="Guest Name" value={guestName} highlight />
                                <InfoRow label="Email Address" value={booking.userEmail || booking.user?.email} />
                                <InfoRow label="Phone Number" value={booking.userPhone} />
                                <InfoRow label="User ID" value={`#${booking.userId || '—'}`} />
                                <InfoRow label="Booking Channel" value={booking.bookingType ? booking.bookingType.toUpperCase() : 'DIRECT'} />
                                <InfoRow label="Payment Type" value={booking.paymentType ? booking.paymentType.toUpperCase() : 'DEPOSIT'} isLast />
                            </Stack>
                        </Box>
                    </Paper>

                    {/* SIDEBAR CARD 3: REGISTERED GUEST MEMBERS */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2, px: 2.25, borderBottom: '1px solid #EAE5D9', bgcolor: '#FAF8F5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Users size={16} color="#6E8964" />
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2D1810' }}>
                                    Guest Members
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#78716C' }}>
                                {booking.members?.length || 0} Registered
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.25 }}>
                            {booking.members && booking.members.length > 0 ? (
                                <Stack spacing={1}>
                                    {booking.members.map((member, index) => (
                                        <Box
                                            key={member.id || index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 1.25,
                                                px: 1.5,
                                                bgcolor: '#FBFBFB',
                                                borderRadius: '6px',
                                                border: '1px solid #EAE5D9'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#EAE5D9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <User size={14} color="#78716C" />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 500, color: '#1E293B', fontSize: '0.84rem', textTransform: 'capitalize' }}>
                                                        {member.name || 'Guest'}
                                                    </Typography>
                                                    <Typography sx={{ color: '#78716C', fontSize: '0.72rem' }}>
                                                        Guest #{index + 1}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {member.id && (
                                                <Chip
                                                    size="small"
                                                    label={`ID: ${member.id}`}
                                                    sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#FFFFFF', border: '1px solid #EAE5D9' }}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ py: 2, textAlign: 'center', bgcolor: '#FBFBFB', borderRadius: '6px' }}>
                                    <Typography sx={{ color: '#78716C', fontSize: '0.82rem' }}>No individual guest members listed.</Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* SIDEBAR CARD 4: PLATFORM VISIBILITY & PARTNERSHIP */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2, px: 2.25, borderBottom: '1px solid #EAE5D9', bgcolor: '#FAF8F5' }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2D1810' }}>
                                Partnership & Visibility
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.25 }}>
                            <Stack spacing={0}>
                                <InfoRow
                                    label="Visibility Plan"
                                    value={booking.visibilityPlanId ? `Plan ID #${booking.visibilityPlanId}` : 'Standard'}
                                />
                                <InfoRow
                                    label="Commission Rate"
                                    value={booking.visibilityCommission ? `${booking.visibilityCommission}%` : '0.00%'}
                                    valueColor="#2E7D32"
                                    highlight
                                />
                                <InfoRow
                                    label="Coordination Fee"
                                    value={booking.coordinationCharges ? `${currency} ${booking.coordinationCharges}` : 'None'}
                                />
                                <InfoRow
                                    label="Affiliate Code"
                                    value={booking.affiliateCode || 'Direct (None)'}
                                />
                                <InfoRow
                                    label="Custom Offer"
                                    value={booking.customOfferId ? `Offer #${booking.customOfferId}` : 'None'}
                                    isLast
                                />
                            </Stack>
                        </Box>
                    </Paper>

                    {/* SIDEBAR CARD 5: SYSTEM AUDIT & HISTORY */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid #EAE5D9',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2, px: 2.25, borderBottom: '1px solid #EAE5D9', bgcolor: '#FAF8F5' }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2D1810' }}>
                                System Audit & Attribution
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2.25 }}>
                            <Stack spacing={0}>
                                <InfoRow
                                    label="Created By"
                                    value={`${booking.cuName || 'User'} (${booking.cuType || 'user'} #${booking.cuId || '—'})`}
                                />
                                <InfoRow
                                    label="Created At"
                                    value={formatDateTime(booking.createdAt)}
                                />
                                <InfoRow
                                    label="Updated By"
                                    value={`${booking.uuName || 'User'} (${booking.uuType || 'user'} #${booking.uuId || '—'})`}
                                />
                                <InfoRow
                                    label="Updated At"
                                    value={formatDateTime(booking.updatedAt)}
                                    isLast
                                />
                            </Stack>
                        </Box>
                    </Paper>

                </Stack>
            </Box>
        </Box>
    );
};

export default BookingDetails;
